import { TranslationsContext } from '@teliaee/telia-translate';
import { action, IReactionDisposer, observable, reaction, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { AnalyticsProvider, ISuggestionItem, LargeSearch, SuggestionField } from 'telia-front-react';
import { v4 } from 'uuid';
import Config from '../Config';
import LanguageStore from '../stores/LanguageStore';
import RouteStore from '../stores/RouteStore';
import SearchStore, { EMPLOYEE_CATEGORY, ESHOP_CATEGORY } from '../stores/SearchStore';
import SsoStore from '../stores/SsoStore';
import { FetchStatus } from '../util/FetchStatus';
import { DealerNewsFilterOrder, SearchEvent, SearchSuggestionWrapper, SuggestionGroup, WebContentFilterOrder } from './SearchInterfaces';

interface SearchInputProps {
  searchStore?: SearchStore;
  routeStore?: RouteStore;
  ssoStore?: SsoStore;
  initialSearch?: string;
  languageStore?: LanguageStore;
}

@observer
class SearchInput extends React.Component<SearchInputProps> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;
  @observable accessor searchFieldValue: string;
  private disposer: IReactionDisposer = null;
  private navigationReaction: IReactionDisposer = null;

  public componentDidMount(): void {
    const { searchStore, initialSearch, ssoStore, routeStore } = this.props;

    if (initialSearch) {
      runInAction(() => {
        this.searchFieldValue = initialSearch;
        searchStore.query = this.searchFieldValue;
        searchStore.fetchSuggestions(initialSearch, searchStore.isOnlyShopSearch);
      });
      this.getSearchResultsByString(initialSearch, true);
    } else {
      runInAction(() => {
        this.searchFieldValue = '';
      });
    }

    this.navigationReaction = reaction(
      () => routeStore.decodedParams,
      (decodedParams) => this.reactToNavigation(decodedParams)
    );

    this.disposer = reaction(
      () => ssoStore.isDealer,
      () => {
        if (this.props.ssoStore.isDealer && !!this.searchFieldValue) {
          Promise.all([
            searchStore.fetchDealerSearchResults(this.searchFieldValue),
            searchStore.fetchDealerSearchResults(this.searchFieldValue, undefined, true)
          ])
            .then(searchStore.setInitialTab)
            .then(searchStore.setInitialArchivedFilter);
        } else {
          searchStore.setInitialTab();
          searchStore.setInitialArchivedFilter();
        }
      }
    );
  }

  public componentWillUnmount(): void {
    const { searchStore } = this.props;
    searchStore.suggestionsWrapper.results = [];
    if (this.disposer) {
      this.disposer();
    }
    if (this.navigationReaction) {
      this.navigationReaction();
    }
  }

  private showDealerSuggestions(): boolean {
    const { ssoStore } = this.props;
    return ssoStore.isDealer && this.searchFieldValue?.length < 3;
  }

  private getSearchSuggestions(): SearchSuggestionWrapper {
    const { searchStore } = this.props;
    return this.showDealerSuggestions() ? searchStore.dealerSearchSuggestionsWrapper : searchStore.suggestionsWrapper;
  }

  public render() {
    const { searchStore } = this.props;
    const translate = this.context.translate;
    const suggestionItems = this.getSearchSuggestions().results?.map((suggestion: SuggestionGroup) => ({
      items: suggestion.items,
      label: translate(suggestion.key)
    }));

    return (
      <AnalyticsProvider group="Site Search">
        <LargeSearch id="searchForm" onSubmit={this.searchAndPushToRouteStore}>
          <SuggestionField
            input={{ maxLength: 256 }}
            isClearable={true}
            id="site-search"
            onChange={this.updateSearchFieldValue}
            label={translate('search.insertQuery')}
            showSuggestionsOnFocus={this.showDealerSuggestions()}
            items={suggestionItems}
            onSelect={this.showDealerSuggestions() ? this.navigateToDealerSuggestion : this.navigateToSuggestionAndSendAlgoliaAnalytics}
            value={this.searchFieldValue}
            isLoading={!suggestionItems?.length && (searchStore.isDebouncing(this.searchFieldValue) || searchStore.isAnyCategoryFetching)}
            minValueLength={3}
          />
        </LargeSearch>
      </AnalyticsProvider>
    );
  }

  private updateSearchFieldValue = (value: string) => {
    runInAction(() => {
      const { isOnlyShopSearch } = this.props.searchStore;
      this.searchFieldValue = value;
      this.props.searchStore.debouncedFetchSearchSuggestions(value, isOnlyShopSearch);
    });
  };

  private navigateToSuggestionAndSendAlgoliaAnalytics = (input: ISuggestionItem) => {
    const isShopUrl = input.url.startsWith(Config.teliaShopUrl);
    const algoliaAnalyticsData = isShopUrl ? this.getAlgoliaAnalyticsData('shop', input) : this.getAlgoliaAnalyticsData('web', input);
    const searchParam = isShopUrl
      ? btoa(JSON.stringify({ ...algoliaAnalyticsData, positions: undefined, eventName: 'Shop search add to cart', eventType: 'conversion' }))
      : undefined;
    this.props.searchStore.postSearchResultClickEvent(algoliaAnalyticsData);
    this.props.routeStore.push(searchParam ? `${input.url}?search=${searchParam}` : input.url);
  };

  private navigateToDealerSuggestion = (input: ISuggestionItem) => {
    this.getSearchResults(this.searchFieldValue);
    this.props.routeStore.push(input.url);
  };

  private getAlgoliaAnalyticsData = (type: 'web' | 'shop', item: ISuggestionItem): SearchEvent => {
    const {
      searchStore: {
        suggestionsWrapper: { results, queryIds }
      }
    } = this.props;
    const isShop = type === 'shop';
    return {
      eventName: `${type.charAt(0).toUpperCase() + type.slice(1)} search suggestion click`,
      eventType: 'click',
      userToken: v4(),
      queryID: queryIds[type],
      index: `${Config.componentTier}_${type}`,
      objectIDs: isShop ? [decodeURIComponent(item.url.split('/').pop())] : [item.url.split(Config.teliaWebUrl).pop()],
      positions: isShop ? [results[0].items.indexOf(item) + 1] : [results[results.length === 1 ? 0 : 1].items.indexOf(item) + 1]
    };
  };

  @action
  private reactToNavigation(decodedParams: { [param: string]: string }) {
    const onlyShopSearchParamValue: boolean = decodedParams.onlyShopSearch === 'true';

    if (decodedParams.query && (decodedParams.query !== this.searchFieldValue || onlyShopSearchParamValue !== this.props.searchStore.isOnlyShopSearch)) {
      this.searchFieldValue = decodedParams.query;
      this.props.searchStore.setIsOnlyShopSearch(onlyShopSearchParamValue);
      this.getSearchResults(this.searchFieldValue);
    }
  }

  private search = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    this.getSearchResults(this.searchFieldValue);
  };

  private getSearchResults = (searchString: string) => {
    const { searchStore } = this.props;
    runInAction(() => {
      searchStore.query = searchString;
      searchStore.allContentResults.filterOrder = WebContentFilterOrder.ALL;
      searchStore.resetArchives();
    });
    if (this.searchFieldValue) {
      this.getSearchResultsByString(searchString, false);
    }
  };

  private searchAndPushToRouteStore = async (event: React.FormEvent<HTMLFormElement>) => {
    document.getElementById('site-search')?.blur();
    event.preventDefault();
    const { routeStore, searchStore } = this.props;
    const trimmedSearchFieldValue = this.searchFieldValue?.trim();
    if (trimmedSearchFieldValue) {
      await searchStore.fetchInitialTab(trimmedSearchFieldValue);
      const predefinedTab = searchStore.initialTab;
      const hasPredefinedNonEshopTab = !!predefinedTab && predefinedTab !== ESHOP_CATEGORY;
      this.search(event);
      routeStore.push(routeStore.pathname, {
        query: trimmedSearchFieldValue,
        sort: searchStore.getEShopProductSortOrder().toLowerCase(),
        onlyShopSearch: searchStore.isOnlyShopSearch && !hasPredefinedNonEshopTab ? 'true' : undefined
      });
    }
  };

  private getSearchResultsByString = (searchString: string, isInitialSearch: boolean) => {
    const {
      searchStore,
      ssoStore,
      languageStore: { currentLanguage }
    } = this.props;

    const promises = [searchStore.fetchEShopSearchResults(searchString, false), searchStore.fetchEShopSearchResults(searchString, true)];
    if (!searchStore.isOnlyShopSearch) {
      promises.push(
        searchStore.fetchContentResultsCount(),
        searchStore.fetchDocumentsOrServices(searchString, true, FetchStatus.FETCHING, true),
        searchStore.fetchDocumentsOrServices(searchString, false, FetchStatus.FETCHING, true),
        searchStore.fetchOnlineHelpResults(searchString),
        searchStore.fetchSelfServiceResults(searchString, currentLanguage),
        searchStore.fetchNewsResults(searchString),
        searchStore.fetchNewsResults(searchString, undefined, true),
        searchStore.fetchInitialTab(searchString)
      );
      if (ssoStore.isDealer) {
        if (!isInitialSearch) {
          runInAction(() => {
            searchStore.searchWrappers[EMPLOYEE_CATEGORY].filterOrder = DealerNewsFilterOrder.ALL;
          });
        }
        promises.push(searchStore.fetchDealerSearchResults(searchString), searchStore.fetchDealerSearchResults(searchString, undefined, true));
      }
    }
    Promise.all(promises).then(searchStore.setInitialTab).then(searchStore.setInitialArchivedFilter);
  };
}

export default inject('searchStore', 'ssoStore', 'routeStore', 'languageStore')(SearchInput);
